import React, { Component, ReactNode } from 'react';
import { useT } from '../lib/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/** Inner functional component that can use hooks */
const ErrorFallback: React.FC<{
  error: Error | null;
  onReset: () => void;
  onReload: () => void;
  onClearData: (confirmMsg: string) => void;
}> = ({ error, onReset, onReload, onClearData }) => {
  const { t } = useT();

  const handleClearData = () => {
    onClearData(t('errorBoundary.clearDataConfirm'));
  };
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4 sm:p-6">
      <div className="bg-surface-2 border border-border rounded-2xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <svg className="w-14 h-14 mx-auto mb-4 text-danger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
          <h2 className="text-2xl sm:text-3xl font-bold text-danger mb-2">
            {t('errorBoundary.title')}
          </h2>
          <p className="text-text-muted text-sm sm:text-base">
            {t('errorBoundary.message')}
          </p>
        </div>

        {/* Error Message */}
        <div className="bg-danger/10 border border-danger/30 rounded-xl p-4 mb-6">
          <div className="text-danger font-mono text-sm break-words">
            {error?.message || t('errorBoundary.unexpectedError')}
          </div>
        </div>

        {/* Development Info */}
        {isDev && error?.stack && (
          <details className="mb-6">
            <summary className="cursor-pointer text-text-muted hover:text-text-primary text-sm font-medium mb-2">
              {t('errorBoundary.developerInfo')}
            </summary>
            <div className="bg-bg border border-border rounded-lg p-3 overflow-x-auto">
              <pre className="text-xs text-text-muted whitespace-pre-wrap font-mono">
                {error.stack}
              </pre>
            </div>
          </details>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={onReset}
            className="w-full py-3 px-4 bg-brand text-white rounded-xl font-bold hover:bg-brand/90 transition-all"
          >
            {t('errorBoundary.retryButton')}
          </button>

          <button
            onClick={onReload}
            className="w-full py-3 px-4 bg-bg text-text-primary border border-border rounded-xl font-medium hover:bg-surface-2 transition-all"
          >
            {t('errorBoundary.reloadButton')}
          </button>

          <button
            onClick={handleClearData}
            className="w-full py-3 px-4 bg-transparent text-danger border border-danger/30 rounded-xl font-medium hover:bg-danger/10 transition-all"
          >
            {t('errorBoundary.clearDataButton')}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-brand/10 border border-brand/30 rounded-lg">
          <p className="text-xs text-text-muted m-0">
            <strong>{t('errorBoundary.solutionTitle')}</strong>
          </p>
          <ul className="text-xs text-text-muted mt-2 ml-4 space-y-1">
            <li>{t('errorBoundary.solution1')}</li>
            <li>{t('errorBoundary.solution2')}</li>
            <li>{t('errorBoundary.solution3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });

    try {
      const errorLog = {
        timestamp: new Date().toISOString(),
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      };
      const logs = JSON.parse(localStorage.getItem('error_logs') || '[]');
      logs.push(errorLog);
      localStorage.setItem('error_logs', JSON.stringify(logs.slice(-10)));
    } catch (e) {
      // localStorage failure ignored
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleClearData = (confirmMsg: string) => {
    if (window.confirm(confirmMsg)) {
      localStorage.clear();
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
          onReload={this.handleReload}
          onClearData={this.handleClearData}
        />
      );
    }

    return this.props.children;
  }
}
